import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2 } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import PermissionGuard from '../../components/PermissionGuard';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { ProductCategory } from '../../types';
import { productService } from '../../services/productService';
import './ProductCategoryListPage.css';

const ProductCategoryListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await productService.getCategories({ pageSize: 100 });
      if (response.data.data) {
        setCategories(response.data.data as ProductCategory[]);
      }
      setLoading(false);
    } catch (err) {
      setError(t('productCategory.failedToLoadCategory'));
      setLoading(false);
    }
  };

  const handleDelete = (id: number) => {
    navigate(`/products/categories/${id}/delete`);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await productService.getCategories({ pageSize: 100 });
      if (response.data.data) {
        setCategories(response.data.data as ProductCategory[]);
      }
      setError(null);
    } catch (err) {
      setError(t('productCategory.failedToLoadCategory'));
    } finally {
      setRefreshing(false);
    }
  };

  // Build lookup BEFORE columns so the render closures can use it
  const categoryById = new Map(categories.map(c => [c.id, c.name]));

  const columns: GridColumn[] = [
    {
      key: 'name',
      label: t('productCategory.categoryName'),
      sortable: true,
      width: '220px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'parentCategoryId',
      label: t('productCategory.parentCategory'),
      sortable: true,
      width: '150px',
      render: (value) => (
        <span className="description-text">
          {value ? (categoryById.get(value) ?? '') : ''}
        </span>
      ),
    },
    {
      key: 'description',
      label: t('productCategory.description'),
      sortable: true,
      width: '280px',
      hideTablet: true,
      hideMobile: true,
      render: (value) => <span className="description-text">{value || 'N/A'}</span>,
    },
    {
      key: 'status',
      label: t('productCategory.status'),
      width: '120px',
      render: () => <span className="badge badge-active">{t('productCategory.active')}</span>,
    },
    {
      key: 'createdAt',
      label: t('productCategory.createdDate'),
      sortable: true,
      width: '140px',
      hideMobile: true,
      render: (value) => new Date(value || new Date()).toLocaleDateString(t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US'),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/products/categories/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    ...(hasPermission(Permissions.Products.Edit)
      ? [{
          icon: (
            <SquarePen size={16} strokeWidth={1.75} />
          ),
          onClick: (item: ProductCategory) => navigate(`/products/categories/${item.id}/edit`),
          className: 'grid-action-btn-edit',
          title: t('common.edit'),
        }]
      : []),
    ...(hasPermission(Permissions.Products.Delete)
      ? [{
          icon: (
            <Trash2 size={16} strokeWidth={1.75} />
          ),
          onClick: (item: ProductCategory) => handleDelete(item.id),
          className: 'grid-action-btn-delete',
          title: t('common.delete'),
        }]
      : []),
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('productCategory.title')}</h1>
          <div className="header-actions">
            <PermissionGuard claim={Permissions.Products.Create}>
              <button
                className="btn-new"
                onClick={() => navigate('/products/categories/create')}
              >
                <CirclePlus/>
                {t('productCategory.newCategory')}
              </button>
            </PermissionGuard>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('productCategory.refreshCategories')}
            >
              <RefreshCw size={18} strokeWidth={1.75} />
              {refreshing ? t('productCategory.refreshing') : t('productCategory.refreshCategories')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <DataGrid
          data={categories}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('productCategory.noDescriptionProvided')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default ProductCategoryListPage;
